import React from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';

import { useModal } from '../Modal/ModalContext';
import { useTimeModal } from '../TimeModal/TimeModalContext';

import Loading from '../Loading';

import styles from '../../styles/DynamicModal.module.css';

const ChooseRegionForMailing = ({ idCase, onMailingSuccess }) => {
    const { openModal } = useModal();
    const { openTimeModal } = useTimeModal();

    const [regions, setRegions] = React.useState([]);
    // Для каждого региона будем хранить список email-ов: { [regionId]: ['a@…', 'b@…', …], … }
    const [regionEmails, setRegionEmails] = React.useState({});

    const [expandedRegions, setExpandedRegions] = React.useState([]);
    // Для каждого региона — массив выбранных email-ов (снятые галочки убираем): { [regionId]: ['a@…', …], … }
    const [emailSelection, setEmailSelection] = React.useState({});

    const [isLoading, setIsLoading] = React.useState(true);
    const [status, setStatus] = React.useState('');

    const [isShowRegionsForMailing, setIsShowRegionsForMailing] = React.useState(false);
    const [isShowRegionsWithoutMailing, setIsShowRegionsWithoutMailing] = React.useState(false);

    React.useEffect(() => {
        setIsLoading(true);
        axios
        .get('/api/regions')
        .then((response) => {
            if (response.data.length === 0) {
            setStatus('На данный момент в систему не добавлено ни одного региона. Обратитесь к администратору');
            } else {
            setStatus('');
            setRegions(response.data);
            }
        })
        .catch((err) => {
            console.error('Ошибка при получении регионов:', err);
            setStatus(`Ошибка при загрузке регионов. ${err.response?.data.message || err.message}`);
        })
        .finally(() => {
            setIsLoading(false);
        });
    }, [openModal]);

    const [isLoadingEmails, setIsLoadingEmails] = React.useState(false);

    const toggleRegionExpand = (regionId) => {
        const isAlready = expandedRegions.includes(regionId);

        if (isAlready) {
        // скрываем
        setExpandedRegions((prev) => prev.filter((id) => id !== regionId));
        return;
        }

        // разворачиваем
        setExpandedRegions((prev) => [...prev, regionId]);

        // если emails ещё нет в state, грузим из API
        if (!regionEmails[regionId]) {
        setIsLoadingEmails(true);

        axios
            .get(`/api/get-emails-from-region?regionId=${regionId}`)
            .then((resp) => {
            // resp.data — массив объектов { email, note, userDetails }
            const emailsWithDetails = resp.data.map((item) => ({
                email: item.email,
                note: item.note || '', 
                userDetails: item.userDetails || null,
            }));

            setRegionEmails((prev) => {
                const newState = { ...prev, [regionId]: emailsWithDetails };
                return newState;
            });

            setEmailSelection((prev) => ({ ...prev, [regionId]: [] }));
            })
            .catch((err) => {
            console.error('Ошибка загрузки email-ов:', err);
            // Вы можете здесь вызывать модальное окно об ошибке:
            // openTimeModal(`Ошибка загрузки email-ов: ${err.message}`, 'error');
            })
            .finally(() => {
            setIsLoadingEmails(false);
            });
        }
    };

    // --- Переключение выбора одного email для конкретного региона ---
    const handleEmailToggle = (regionId, email) => {
        setEmailSelection((prev) => {
        const selectedForRegion = prev[regionId] || [];
        const isSelected = selectedForRegion.includes(email);

        const updated = isSelected
            ? selectedForRegion.filter((e) => e !== email)
            : [...selectedForRegion, email];

        return { ...prev, [regionId]: updated };
        });
    };

    // --- Переключение выделения всех email-ов для региона ---
    const toggleSelectAllEmails = (regionId, emailsArray) => {
        setEmailSelection((prev) => {
        const selectedForRegion = prev[regionId] || [];
        // извлекаем только сами адреса email из объектов
        const allEmails = emailsArray.map((obj) => obj.email);
        const allSelected = allEmails.every((em) =>
            selectedForRegion.includes(em)
        );

        return {
            ...prev,
            [regionId]: allSelected ? [] : allEmails,
        };
        });
    };

    const [isAllEmailsSelected, setIsAllEmailsSelected] = React.useState(false);

    const handleSelectAllEmailsFromAllRegions = () => {
        if (isAllEmailsSelected) {
            // Убираем все галочки
            setEmailSelection({});
            setIsAllEmailsSelected(false);
        } else {
            // Раскрываем все регионы
            const regionIds = regions.map((region) => region.id);
            setExpandedRegions(regionIds);

            // Фильтруем регионы, для которых еще не загружены email
            const regionsToLoad = regionIds.filter((regionId) => !regionEmails[regionId]);

            if (regionsToLoad.length === 0) {
                // Если все email уже загружены, просто отмечаем их
                const allEmailsSelected = regionIds.reduce((acc, regionId) => {
                    const emailsForRegion = (regionEmails[regionId] || []).map((item) => item.email);
                    acc[regionId] = emailsForRegion;
                    return acc;
                }, {});
                setEmailSelection(allEmailsSelected);
                setIsAllEmailsSelected(true);
                return;
            }

            // Загружаем email для всех регионов, которые еще не загружены
            const emailRequests = regionsToLoad.map((regionId) =>
                axios.get(`/api/get-emails-from-region?regionId=${regionId}`)
            );

            setIsLoadingEmails(true);

            Promise.all(emailRequests)
                .then((responses) => {
                    // Обновляем email для регионов
                    const emailsData = responses.reduce((acc, resp, index) => {
                        const regionId = regionsToLoad[index];
                        acc[regionId] = resp.data.map((item) => ({
                            email: item.email,
                            note: item.note || '',
                            userDetails: item.userDetails || null,
                        }));
                        return acc;
                    }, {});

                    setRegionEmails((prev) => ({ ...prev, ...emailsData }));

                    // Отмечаем все email для всех регионов
                    const allEmailsSelected = regionIds.reduce((acc, regionId) => {
                        const emailsForRegion = (emailsData[regionId] || regionEmails[regionId] || []).map((item) => item.email);
                        acc[regionId] = emailsForRegion;
                        return acc;
                    }, {});

                    setEmailSelection(allEmailsSelected);
                    setIsAllEmailsSelected(true);
                })
                .catch((err) => {
                    console.error('Ошибка загрузки email-ов:', err);
                    setStatus('Ошибка загрузки email-ов для некоторых регионов.');
                })
                .finally(() => {
                    setIsLoadingEmails(false);
                });
        }
    };


    // Нажатие кнопки Отправить рассылку
    const handleSendMailing = () => {
        const allEmails = Object.values(emailSelection).reduce((acc, emails) => acc.concat(emails), []);

        if (allEmails.length === 0) {
            openTimeModal('Необходимо выбрать хотя бы один адрес электронной почты', 'error');
            setTimeout(() => setStatus(''), 4000);
            return;
        }

        setIsLoading(true);
        axios.post('/api/send-offers-case', {
            emails: allEmails,
            idCase: idCase
        })
        .then(() => {
            setStatus('Рассылка успешно выполнена.');
            setTimeout(() => {
                setStatus('');
                if (onMailingSuccess) onMailingSuccess();
            }, 2300);
        })
        .catch((err) => {
            console.error('Ошибка при отправке рассылки:', err);
            setStatus(err.response?.data.message || err.message);
            setTimeout(() => {
                setStatus('');
            }, 4000);
        })
        .finally(() => {
            setIsLoading(false);
        });
    };

    const appointAMWithoutMailing = (emailAM) => {
        setIsLoading(true);
        axios.put('/api/assign-am-to-case', {
            emailAM: emailAM,
            idCase: idCase,
        })
       .then(() => {
            onMailingSuccess();
            openTimeModal('Арбитражный управляющий успешно выбран в обход рассылки', 'success');
       })
       .catch((error) => {
            console.error(error);
            openTimeModal('Произошла неизвестная ошибка. Повторите попытку позднее', 'error');
        })
        .finally(() => {
            setIsLoading(false);
        })
    };

    return (
        <>
            {isLoading ? (
                <Loading />
            ) : ( isShowRegionsForMailing ? (
                <>
                    {!status && (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', zIndex: '5', background: 'linear-gradient(180deg,rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0.47) 100%)' }}>
                            <h2 style={{ display: 'flex', justifyContent: 'space-between', margin: 0 }}>
                                Выберите адреса электронной почты для рассылки {isLoadingEmails && <Loading widthParam="mini" />}
                            </h2>
                            <img
                                src="/img/icons/close-x.svg"
                                alt="X"
                                style={{ width: '20px', cursor: 'pointer' }}
                                onClick={() => {
                                setIsShowRegionsForMailing(false);
                                }}
                            />
                        </div>
                        <div className={styles.outliningPanel} style={{display: 'flex', flexDirection: 'row'}}>
                            <a style={{width: '100%'}}>
                                Отметить все адреса во всех регионах
                            </a>
                            <button type='button' className='miniButton' style={{width: '40%', marginLeft: '10px'}} onClick={() => {handleSelectAllEmailsFromAllRegions()}}>
                                {isAllEmailsSelected ? 'Убрать все' : 'Отметить все'}
                            </button>
                        </div>
                        {isAllEmailsSelected && 
                            <a className={`${styles.statusPanel} ${styles.red}`} style={{position: 'sticky', top: '5px'}}>
                                Обратите внимание: вы выбрали все адреса арбитражных управляющих из всех регионов. Убедитесь в правильности вашего выбора, так как отправку рассылки отменить будет невозможно.
                            </a>
                        }
                        {regions.map((region) => {
                            const emailsForRegion = regionEmails[region.id] || [];
                            const isRegionExpanded = expandedRegions.includes(region.id);
                            const isLoadingRegionEmails = isLoadingEmails && !regionEmails[region.id];
                            const areAllEmailsSelected = emailsForRegion.every(({ email }) =>
                                (emailSelection[region.id] || []).includes(email)
                            );

                            return (
                            <div key={region.id} className={styles.dropdownList}>
                                <div className={styles.headForDdList} onClick={() => toggleRegionExpand(region.id)}>
                                    <a style={{ fontWeight: '600' }}>{region.name}</a>
                                    <a>
                                        {isRegionExpanded ? <svg xmlns="http://www.w3.org/2000/svg" id="Isolation_Mode" data-name="Isolation Mode" viewBox="0 0 24 24"><path d="M0,16.019,9.52,6.513a3.507,3.507,0,0,1,4.948,0L24,16.031l-2.121,2.117L12.348,8.63a.5.5,0,0,0-.707,0l-9.52,9.507Z"/></svg> : <svg xmlns="http://www.w3.org/2000/svg" data-name="Isolation Mode" viewBox="0 0 24 24"><path d="M0,8.057l9.52,9.507a3.507,3.507,0,0,0,4.948,0L24,8.046,21.879,5.929l-9.531,9.517a.5.5,0,0,1-.707,0L2.121,5.94Z"/></svg>}
                                    </a>
                                </div>
                                {isRegionExpanded && (
                                    isLoadingRegionEmails ? (
                                        <div>Загрузка адресов...</div>
                                    ) : (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 0.3 }}
                                        style={{width: '100%'}}
                                    >
                                        <div className={styles.headForDdList} onClick={() => toggleSelectAllEmails(region.id, emailsForRegion)} style={{borderTop: '1px solid rgb(204, 204, 204)'}}>
                                            <a>Отметить все</a>
                                            <input
                                            type="checkbox"
                                            checked={areAllEmailsSelected} />
                                        </div>
                                        <div className={styles.expandedPartOfTheList}>
                                            {emailsForRegion.map((data) => (
                                            <div key={data.email} className={styles.emailLine} onClick={() => handleEmailToggle(region.id, data.email)}>
                                                <div style={{ width: '85%' }}>
                                                    <a style={{ wordBreak: 'break-all' }}>{data.email}</a>
                                                    {data.userDetails ? (
                                                        <a className={styles.userDetails}>
                                                            {data.userDetails.lastname} {data.userDetails.firstname} {data.userDetails.surname}
                                                        </a>
                                                    ) : (
                                                        <a className={styles.userDetails} onClick={() => {console.log(data.userDetails)}}>Пользователь не зарегистрирован</a>
                                                    )}
                                                    <a className="description">{data.note || 'Нет примечаний'}</a>
                                                </div>
                                                <input type="checkbox" checked={(emailSelection[region.id] || []).includes(data.email)} />
                                            </div>
                                            ))}
                                        </div>
                                    </motion.div>
                                    )
                                )}
                            </div>
                            );
                        })}
                        <a onClick={handleSendMailing} className="button" style={{position: 'sticky', bottom: '5px', boxShadow: '0px 0px 10px 5px white'}} disabled={isLoading || Object.keys(emailSelection).length === 0}>
                            Отправить рассылку
                        </a>
                        <a className="description">Обратите внимание, это действие необратимо</a>
                    </>
                    )}
                    {status && (
                    <a className={styles.statusPanel}>
                        {status}
                    </a>
                    )}
                </>
                ) : (
                    isShowRegionsWithoutMailing ? (
                        (
                            <>
                            {!status && (
                                <>
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            marginBottom: "10px",
                                        }}
                                    >
                                        <h2 style={{ margin: "0" }}>
                                            Выберите адрес электронной почты арбитражного управляющего
                                        </h2>
                                        <img
                                            src="/img/icons/close-x.svg"
                                            alt="X"
                                            style={{ width: "20px", cursor: "pointer" }}
                                            onClick={() => {
                                                setIsShowRegionsWithoutMailing(false);
                                            }}
                                        />
                                    </div>
                                    {regions.map((region) => {
                                        const emailsForRegion = regionEmails[region.id] || [];
                                        const isRegionExpanded = expandedRegions.includes(region.id);
                                        const isLoadingRegionEmails = isLoadingEmails && !regionEmails[region.id];

                                        return (
                                            <div key={region.id} className={styles.dropdownList}>
                                                <div
                                                    className={styles.headForDdList}
                                                    onClick={() => toggleRegionExpand(region.id)}
                                                >
                                                    <a style={{ fontWeight: "600" }}>{region.name}</a>
                                                    <a>
                                                        {isRegionExpanded ? (
                                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M0,16.019,9.52,6.513a3.507,3.507,0,0,1,4.948,0L24,16.031l-2.121,2.117L12.348,8.63a.5.5,0,0,0-.707,0l-9.52,9.507Z" /></svg>
                                                        ) : (
                                                            <svg xmlns="http://www.w3.org/2000/svg"viewBox="0 0 24 24"><path d="M0,8.057l9.52,9.507a3.507,3.507,0,0,0,4.948,0L24,8.046,21.879,5.929l-9.531,9.517a.5.5,0,0,1-.707,0L2.121,5.94Z" /></svg>
                                                        )}
                                                    </a>
                                                </div>
                                                {isRegionExpanded && (
                                                    isLoadingRegionEmails ? (
                                                        <div>Загрузка адресов...</div>
                                                    ) : (
                                                        <motion.div
                                                            initial={{ opacity: 0 }}
                                                            animate={{ opacity: 1 }}
                                                            exit={{ opacity: 0 }}
                                                            transition={{ duration: 0.3 }}
                                                            style={{ width: "100%" }}
                                                        >
                                                            <div className={styles.expandedPartOfTheList}>
                                                                {emailsForRegion.map((data) => (
                                                                    <div key={data.email} className={styles.emailLine} style={{flexDirection: 'column'}}>
                                                                        <div style={{width: '100%', cursor: 'default'}}>
                                                                            <a style={{wordBreak: "break-all"}}>
                                                                                {data.email}
                                                                            </a>
                                                                            {data.userDetails ? (
                                                                                <a className={styles.userDetails}>
                                                                                    {data.userDetails.lastname} {data.userDetails.firstname} {data.userDetails.surname}
                                                                                </a>
                                                                            ) : (
                                                                                <a className={styles.userDetails} onClick={() => {console.log(data.userDetails)}}>Пользователь не зарегистрирован</a>
                                                                            )}
                                                                            <a className="description">
                                                                                {data.note || "Нет примечаний"}
                                                                            </a>
                                                                        </div>
                                                                        {data.userDetails && <button type="button" className="button miniButton" onClick={() => {appointAMWithoutMailing(data.email)}}>Выбрать</button>}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </motion.div>
                                                    )
                                                )}
                                            </div>
                                        );
                                    })}
                                </>
                            )}
                            {status && (
                                <a className={styles.statusPanel}>
                                    <img src="/img/icons/check-circle.svg" alt="Статус" />
                                    {status}
                                </a>
                            )}
                        </>
                        )
                    ) 
                    :
                    (
                    <>
                        <a>На данный момент не выбран</a>
                        <button type='button' className='button black' onClick={() => {setIsShowRegionsForMailing(true)}}>Выбрать регион для рассылки</button>
                        <button type='button' className='button miniButton' onClick={() => {setIsShowRegionsWithoutMailing(true)}}>Выбрать арбитражного управляющего в обход рассылки</button>
                    </>
                    )
                )
            )}
        </>
    );
};

export default ChooseRegionForMailing;
